import React from 'react';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  GiCrossedSwords, GiDeathSkull, GiTrophy, GiSoulVessel,
  GiLaurelsTrophy, GiPodiumWinner
} from 'react-icons/gi';
import { FiZap, FiUsers } from 'react-icons/fi';
import './GameStats.css';

// Register Chart.js components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Skywars statistics display component
 */
const SkywarsStats = ({ stats }) => {
  const formatNumber = (num) => num?.toLocaleString() || '0';

  // Remove color codes from level string
  const cleanLevel = (level) => {
    if (typeof level === 'string') {
      return level.replace(/[^0-9*]/g, '');
    }
    return level || 0;
  };

  // Chart data for wins/losses
  const wlData = {
    labels: ['Wins', 'Losses'],
    datasets: [{
      data: [stats?.wins || 0, stats?.losses || 0],
      backgroundColor: ['#10b981', '#ef4444'],
      borderWidth: 0
    }]
  };

  // Chart data for K/D
  const kdData = {
    labels: ['Kills', 'Deaths'],
    datasets: [{
      label: 'Count',
      data: [stats?.kills || 0, stats?.deaths || 0],
      backgroundColor: ['#8b5cf6', '#ef4444'],
      borderRadius: 6
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#a1a1aa',
          padding: 16,
          font: { size: 12 }
        }
      }
    }
  };

  const barOptions = {
    ...chartOptions,
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#a1a1aa' }
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#a1a1aa' }
      }
    }
  };

  const statItems = [
    { icon: <GiLaurelsTrophy />, label: 'Level', value: cleanLevel(stats?.level), color: '#9b59b6' },
    { icon: <GiTrophy />, label: 'Wins', value: formatNumber(stats?.wins), color: '#10b981' },
    { icon: <GiDeathSkull />, label: 'Losses', value: formatNumber(stats?.losses), color: '#ef4444' },
    { icon: <GiCrossedSwords />, label: 'Kills', value: formatNumber(stats?.kills), color: '#8b5cf6' },
    { icon: <GiPodiumWinner />, label: 'Deaths', value: formatNumber(stats?.deaths), color: '#f472b6' },
    { icon: <FiUsers />, label: 'Assists', value: formatNumber(stats?.assists), color: '#06b6d4' },
    { icon: <GiSoulVessel />, label: 'Souls', value: formatNumber(stats?.souls), color: '#a78bfa' },
    { icon: <FiZap />, label: 'Games Played', value: formatNumber(stats?.gamesPlayed), color: '#f59e0b' }
  ];

  const ratioItems = [
    { label: 'KDR', value: stats?.kdr || '0.00', description: 'Kill/Death Ratio' },
    { label: 'WLR', value: stats?.wlr || '0.00', description: 'Win/Loss Ratio' }
  ];

  return (
    <motion.div
      className="game-stats skywars-stats"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Ratios Section */}
      <div className="ratios-section">
        <h3 className="section-title">Key Ratios</h3>
        <div className="ratios-grid compact">
          {ratioItems.map((ratio, index) => (
            <motion.div
              key={ratio.label}
              className="ratio-card large"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <span className="ratio-value">{ratio.value}</span>
              <span className="ratio-label">{ratio.label}</span>
              <span className="ratio-description">{ratio.description}</span>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-section">
        <h3 className="section-title">Statistics</h3>
        <div className="stats-grid">
          {statItems.map((stat, index) => (
            <motion.div
              key={stat.label}
              className="mini-stat-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="mini-stat-icon" style={{ color: stat.color }}>
                {stat.icon}
              </div>
              <div className="mini-stat-info">
                <span className="mini-stat-value">{stat.value}</span>
                <span className="mini-stat-label">{stat.label}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <h3 className="section-title">Visual Breakdown</h3>
        <div className="charts-grid">
          <div className="chart-card">
            <h4 className="chart-title">Win/Loss Distribution</h4>
            <div className="chart-container doughnut">
              <Doughnut data={wlData} options={chartOptions} />
            </div>
          </div>
          <div className="chart-card">
            <h4 className="chart-title">Kills vs Deaths</h4>
            <div className="chart-container bar">
              <Bar data={kdData} options={barOptions} />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SkywarsStats;
